import { useCallback, useContext } from "react";
import { sessionStore } from "../../../../contexts/SessionContext";
import useAthenaAPIClient from "../../../../hooks/queries/useAthenaAPIClient/useAthenaAPIClient";
import {
    Dashboard,
    ClassroomAnalytics as DashboardClassroom,
} from "../../../../interfaces/Dashboard";
import { Classroom as UserClassroom } from "../../../../interfaces/User";
import { Group as SessionClassroom } from "../../../../interfaces/Session";
import { completeLockStatus } from "../../../../utils/pedagogical-ressources";
import { useQueryClient } from "@tanstack/react-query";
import queryKeys from "../../../../hooks/queries/useAthenaAPIClient/queryKeys";
import { useUserId } from "../../../../hooks/useUserInfo";
import { useClassrooms } from "../../../../hooks/useSession";

type ClassroomManagementReturn = {
    createClassroom: (
        classroom: Omit<
            UserClassroom,
            "id" | "students" | "teacher" | "lock_status" | "code"
        > & { createCode: boolean; teachers: string[] }
    ) => Promise<UserClassroom>;
};

const useClassroomManagement = (): ClassroomManagementReturn => {
    const userId = useUserId();
    const { setSession } = useContext(sessionStore);
    const athenaAPIClient = useAthenaAPIClient();
    const classroomsList = useClassrooms();
    const queryClient = useQueryClient();
    const createClassroom = useCallback<
        ClassroomManagementReturn["createClassroom"]
    >(
        async (classroom) => {
            const newClassroom = await athenaAPIClient.createClassroom(
                classroom
            );

            queryClient.setQueryData<Dashboard | undefined>(
                queryKeys.getDashboard(userId, classroomsList),
                (prevData) =>
                    prevData
                        ? {
                              ...prevData,
                              classrooms: [
                                  ...prevData.classrooms,
                                  userClassroomToDashboardClassroom(
                                      newClassroom
                                  ),
                              ],
                          }
                        : undefined
            );
            setSession((session) => ({
                ...session,
                classrooms: [
                    ...(session.classrooms ?? []),
                    userClassroomToSessionClassroom(newClassroom),
                ],
            }));
            return newClassroom;
        },
        [athenaAPIClient, setSession, queryClient, userId, classroomsList]
    );

    return {
        createClassroom,
    };
};
export default useClassroomManagement;

const userClassroomToDashboardClassroom = (
    classroom: UserClassroom
): DashboardClassroom => ({
    id: classroom.id,
    external_id: classroom.external_id,
    code: classroom.code,
    level: classroom.level,
    name: classroom.name,
    studentsNumber: 0,
    alert: {
        isAlert: false,
    },
    modulesList: [],
});

const userClassroomToSessionClassroom = (
    classroom: UserClassroom
): SessionClassroom => ({
    id: classroom.id,
    name: classroom.name,
    lockStatus: completeLockStatus({}),
    students: [],
    external_id: classroom.external_id,
});
